/**
 * Collection Icons New - Standalone JavaScript File
 * Version: 1.0
 * Compatible with: Shopify Liquid Themes
 * Features: Touch/Swipe, Keyboard Navigation, Auto-play, Responsive Design
 */

(function() {
  'use strict';

  // Global namespace
  window.CollectionIconsNew = window.CollectionIconsNew || {};

  /**
   * Main Collection Icons Class
   */
  class CollectionIconsNewSection {
    constructor(sectionId, options = {}) {
      this.sectionId = sectionId;
      this.options = {
        showArrows: true,
        showDots: false,
        autoPlay: false,
        autoPlaySpeed: 5000,
        itemSpacing: 25,
        iconSize: 120,
        ...options
      };

      // DOM Elements
      this.section = document.getElementById(`collection-icons-new-${sectionId}`);
      this.scrollContainer = document.getElementById(`collection-scroll-new-${sectionId}`);
      this.prevButton = document.getElementById(`prev-new-${sectionId}`);
      this.nextButton = document.getElementById(`next-new-${sectionId}`);
      this.dotsContainer = document.getElementById(`dots-new-${sectionId}`);

      if (!this.section || !this.scrollContainer) {
        console.warn(`Collection Icons New: Section ${sectionId} not found`);
        return;
      }

      // State
      this.currentIndex = 0;
      this.isAnimating = false;
      this.isDragging = false;
      this.autoPlayTimer = null;
      this.resizeTimer = null;

      // Touch/Mouse tracking
      this.startX = 0;
      this.startY = 0;
      this.currentX = 0;
      this.currentY = 0;
      this.startTime = 0;

      // Device detection
      this.hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      this.isMobile = false;
      this.visibleItems = 8;
      this.totalItems = 0;
      this.maxIndex = 0;

      this.init();
    }

    /**
     * Initialize the section
     */
    init() {
      this.calculateDimensions();
      this.bindEvents();
      this.updateUI();
      this.setupIntersectionObserver();
      this.applyCSSVariables();

      if (this.options.autoPlay) {
        this.startAutoPlay();
      }

      // Initialize position
      this.scrollToIndex(0, false);
      
      console.log(`Collection Icons New: Section ${this.sectionId} initialized`);
    }

    /**
     * Calculate dimensions and responsive behavior
     */
    calculateDimensions() {
      const items = this.scrollContainer.children;
      this.totalItems = items.length;

      if (this.totalItems === 0) return;

      // Determine if mobile based on viewport width
      this.isMobile = window.innerWidth <= 768;
      this.visibleItems = this.isMobile ? 4 : 8;
      this.maxIndex = Math.max(0, this.totalItems - this.visibleItems);

      // Calculate scroll amount
      const containerWidth = this.scrollContainer.parentElement.offsetWidth;
      const computedStyle = getComputedStyle(this.scrollContainer);
      this.gap = parseInt(computedStyle.gap) || this.options.itemSpacing;

      if (this.isMobile) {
        // On mobile, use natural scroll behavior
        this.itemWidth = items[0]?.offsetWidth || 0;
        this.scrollAmount = this.itemWidth + this.gap;
      } else {
        // On desktop, calculate based on container width
        this.itemWidth = (containerWidth - (this.gap * (this.visibleItems - 1))) / this.visibleItems;
        this.scrollAmount = this.itemWidth + this.gap;
      }
    }

    /**
     * Apply CSS custom properties for dynamic styling
     */
    applyCSSVariables() {
      const root = document.documentElement;
      root.style.setProperty('--collection-icons-new-gap', `${this.options.itemSpacing}px`);
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Navigation arrows
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.scrollPrev());
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollNext());
      }

      // Progress dots
      if (this.dotsContainer) {
        this.dotsContainer.addEventListener('click', (e) => {
          if (e.target.classList.contains('collection-dot-new')) {
            const page = parseInt(e.target.dataset.page);
            this.scrollToPage(page);
          }
        });
      }

      // Touch and mouse events
      this.bindTouchEvents();
      this.bindMouseEvents();

      // Keyboard navigation
      this.scrollContainer.addEventListener('keydown', (e) => this.handleKeyDown(e));

      // Window events
      window.addEventListener('resize', () => this.handleResize());
      window.addEventListener('orientationchange', () => {
        setTimeout(() => this.handleResize(), 100);
      });

      // Visibility change for auto-play
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAutoPlay();
        } else if (this.options.autoPlay) {
          this.startAutoPlay();
        }
      });

      // Section hover for auto-play
      if (this.options.autoPlay) {
        this.section.addEventListener('mouseenter', () => this.pauseAutoPlay());
        this.section.addEventListener('mouseleave', () => this.startAutoPlay());
      }
    }

    /**
     * Bind touch events for mobile interaction
     */
    bindTouchEvents() {
      if (!this.hasTouch) return;

      this.scrollContainer.addEventListener('touchstart', (e) => {
        this.handleInteractionStart(e.touches[0]);
      }, { passive: false });

      this.scrollContainer.addEventListener('touchmove', (e) => {
        this.handleInteractionMove(e.touches[0]);
        
        // Prevent vertical scrolling if horizontal movement is dominant
        if (this.isDragging && Math.abs(this.currentX - this.startX) > Math.abs(this.currentY - this.startY)) {
          e.preventDefault();
        }
      }, { passive: false });

      this.scrollContainer.addEventListener('touchend', (e) => {
        this.handleInteractionEnd(e.changedTouches[0]);
      }, { passive: true });

      // Prevent context menu on long press
      this.scrollContainer.addEventListener('contextmenu', (e) => {
        if (this.isDragging) {
          e.preventDefault();
        }
      });
    }

    /**
     * Bind mouse events for desktop interaction
     */
    bindMouseEvents() {
      this.scrollContainer.addEventListener('mousedown', (e) => {
        if (this.isMobile) return;
        e.preventDefault();
        this.handleInteractionStart(e);
      });

      document.addEventListener('mousemove', (e) => {
        if (!this.isDragging || this.isMobile) return;
        this.handleInteractionMove(e);
      });

      document.addEventListener('mouseup', (e) => {
        if (!this.isDragging || this.isMobile) return;
        this.handleInteractionEnd(e);
      });

      // Prevent text selection during drag
      this.scrollContainer.addEventListener('selectstart', (e) => {
        if (this.isDragging) {
          e.preventDefault();
        }
      });
    }

    /**
     * Handle interaction start (touch/mouse)
     */
    handleInteractionStart(event) {
      if (this.isAnimating) return;

      this.isDragging = true;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.currentX = event.clientX;
      this.currentY = event.clientY;
      this.startTime = Date.now();

      this.scrollContainer.style.cursor = 'grabbing';
      this.pauseAutoPlay();
    }

    /**
     * Handle interaction move (touch/mouse)
     */
    handleInteractionMove(event) {
      if (!this.isDragging) return;

      this.currentX = event.clientX;
      this.currentY = event.clientY;

      const deltaX = this.currentX - this.startX;
      const deltaY = this.currentY - this.startY;

      // Visual feedback during drag (desktop only)
      if (!this.isMobile && Math.abs(deltaX) > 5) {
        const currentTransform = this.currentIndex * this.scrollAmount;
        const newTransform = currentTransform - (deltaX * 0.3);
        this.scrollContainer.style.transform = `translateX(-${newTransform}px)`;
      }
    }

    /**
     * Handle interaction end (touch/mouse)
     */
    handleInteractionEnd(event) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.scrollContainer.style.cursor = '';

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;
      const deltaTime = Date.now() - this.startTime;
      const velocity = Math.abs(deltaX) / deltaTime;

      // Determine if this was a swipe gesture
      const threshold = velocity > 0.5 ? 30 : 50;
      const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > threshold;

      if (isHorizontalSwipe) {
        if (deltaX > 0) {
          this.scrollPrev();
        } else {
          this.scrollNext();
        }
      } else {
        // Snap back to current position
        this.scrollToIndex(this.currentIndex, true);
      }

      if (this.options.autoPlay) {
        this.startAutoPlay();
      }
    }

    /**
     * Handle keyboard navigation
     */
    handleKeyDown(event) {
      if (this.isAnimating) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.scrollPrev();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.scrollNext();
          break;
        case 'Home':
          event.preventDefault();
          this.scrollToIndex(0);
          break;
        case 'End':
          event.preventDefault();
          this.scrollToIndex(this.maxIndex);
          break;
        case ' ': // Space bar
          event.preventDefault();
          if (this.options.autoPlay) {
            this.toggleAutoPlay();
          } else {
            this.scrollNext();
          }
          break;
      }
    }

    /**
     * Handle window resize
     */
    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.calculateDimensions();

        // Reset position if switching between mobile/desktop
        if (wasMobile !== this.isMobile) {
          this.currentIndex = 0;
        }

        // Ensure current index is within bounds
        this.currentIndex = Math.min(this.currentIndex, this.maxIndex);

        this.scrollToIndex(this.currentIndex, false);
        this.updateUI();
      }, 150);
    }

    /**
     * Scroll to previous items
     */
    scrollPrev() {
      if (this.isAnimating || this.currentIndex <= 0) return;

      const newIndex = Math.max(0, this.currentIndex - 1);
      this.scrollToIndex(newIndex);
    }

    /**
     * Scroll to next items
     */
    scrollNext() {
      if (this.isAnimating || this.currentIndex >= this.maxIndex) return;

      const newIndex = Math.min(this.maxIndex, this.currentIndex + 1);
      this.scrollToIndex(newIndex);
    }

    /**
     * Scroll to specific page (for dots navigation)
     */
    scrollToPage(page) {
      const newIndex = Math.min(page, this.maxIndex);
      this.scrollToIndex(newIndex);
    }

    /**
     * Scroll to specific index
     */
    scrollToIndex(index, animate = true) {
      if (this.isAnimating && animate) return;

      const clampedIndex = Math.max(0, Math.min(index, this.maxIndex));

      if (clampedIndex === this.currentIndex && animate) return;

      this.currentIndex = clampedIndex;

      if (animate) {
        this.isAnimating = true;
      }

      const scrollPosition = this.currentIndex * this.scrollAmount;

      if (this.isMobile) {
        // On mobile, use native scroll
        this.scrollContainer.style.transform = '';
        this.scrollContainer.scrollLeft = scrollPosition;
      } else {
        // On desktop, use transform
        this.scrollContainer.style.transform = `translateX(-${scrollPosition}px)`;
      }

      this.updateUI();

      if (animate) {
        // Reset animation flag after transition
        setTimeout(() => {
          this.isAnimating = false;
        }, 400);
      }

      // Dispatch custom event
      this.dispatchEvent('scroll', {
        currentIndex: this.currentIndex,
        totalItems: this.totalItems,
        visibleItems: this.visibleItems
      });
    }

    /**
     * Update UI elements (arrows, dots)
     */
    updateUI() {
      this.updateArrows();
      this.updateDots();
    }

    /**
     * Update navigation arrows
     */
    updateArrows() {
      if (!this.prevButton || !this.nextButton) return;

      // Hide arrows on mobile
      if (this.isMobile) {
        this.prevButton.style.display = 'none';
        this.nextButton.style.display = 'none';
        return;
      }

      this.prevButton.style.display = 'flex';
      this.nextButton.style.display = 'flex';

      // Update arrow states
      this.prevButton.disabled = this.currentIndex <= 0;
      this.nextButton.disabled = this.currentIndex >= this.maxIndex;

      // Update ARIA attributes
      this.prevButton.setAttribute('aria-disabled', this.currentIndex <= 0);
      this.nextButton.setAttribute('aria-disabled', this.currentIndex >= this.maxIndex);
    }

    /**
     * Update progress dots
     */
    updateDots() {
      if (!this.dotsContainer) return;

      const dots = this.dotsContainer.querySelectorAll('.collection-dot-new');
      const currentPage = Math.floor(this.currentIndex / this.visibleItems);

      dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === currentPage);
        dot.setAttribute('aria-pressed', index === currentPage);
      });
    }

    /**
     * Start auto-play
     */
    startAutoPlay() {
      if (!this.options.autoPlay || this.autoPlayTimer) return;

      this.autoPlayTimer = setInterval(() => {
        if (this.currentIndex >= this.maxIndex) {
          this.scrollToIndex(0);
        } else {
          this.scrollNext();
        }
      }, this.options.autoPlaySpeed);
    }

    /**
     * Pause auto-play
     */
    pauseAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer);
        this.autoPlayTimer = null;
      }
    }

    /**
     * Toggle auto-play
     */
    toggleAutoPlay() {
      if (this.autoPlayTimer) {
        this.pauseAutoPlay();
      } else {
        this.startAutoPlay();
      }
    }

    /**
     * Setup intersection observer for performance
     */
    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Section is visible
            if (this.options.autoPlay && !this.autoPlayTimer) {
              this.startAutoPlay();
            }
          } else {
            // Section is not visible
            this.pauseAutoPlay();
          }
        });
      }, { threshold: 0.1 });

      observer.observe(this.section);
    }

    /**
     * Dispatch custom events
     */
    dispatchEvent(eventName, detail) {
      const event = new CustomEvent(`collectionIconsNew:${eventName}`, {
        detail: { sectionId: this.sectionId, ...detail },
        bubbles: true
      });
      this.section.dispatchEvent(event);
    }

    /**
     * Public API methods
     */
    goToSlide(index) {
      this.scrollToIndex(index);
    }

    getCurrentIndex() {
      return this.currentIndex;
    }

    getTotalItems() {
      return this.totalItems;
    }

    getVisibleItems() {
      return this.visibleItems;
    }

    setAutoPlay(enabled, speed) {
      this.options.autoPlay = enabled;
      if (speed) this.options.autoPlaySpeed = speed * 1000;

      if (enabled) {
        this.startAutoPlay();
      } else {
        this.pauseAutoPlay();
      }
    }

    /**
     * Destroy instance and clean up
     */
    destroy() {
      this.pauseAutoPlay();
      
      // Remove event listeners
      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.scrollPrev);
      }
      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.scrollNext);
      }

      // Clear timers
      clearTimeout(this.resizeTimer);

      console.log(`Collection Icons New: Section ${this.sectionId} destroyed`);
    }
  }

  /**
   * Global initialization and management
   */
  const CollectionIconsNewManager = {
    instances: new Map(),

    /**
     * Initialize a section
     */
    init(sectionId, options = {}) {
      if (this.instances.has(sectionId)) {
        console.warn(`Collection Icons New: Section ${sectionId} already initialized`);
        return this.instances.get(sectionId);
      }

      const instance = new CollectionIconsNewSection(sectionId, options);
      this.instances.set(sectionId, instance);
      return instance;
    },

    /**
     * Get instance by section ID
     */
    getInstance(sectionId) {
      return this.instances.get(sectionId);
    },

    /**
     * Destroy instance
     */
    destroy(sectionId) {
      const instance = this.instances.get(sectionId);
      if (instance) {
        instance.destroy();
        this.instances.delete(sectionId);
      }
    },

    /**
     * Initialize all sections on page
     */
    initAll() {
      const sections = document.querySelectorAll('.collection-icons-new-section');
      sections.forEach(section => {
        const sectionId = section.dataset.sectionId;
        if (sectionId && !this.instances.has(sectionId)) {
          this.init(sectionId);
        }
      });
    },

    /**
     * Destroy all instances
     */
    destroyAll() {
      this.instances.forEach((instance, sectionId) => {
        this.destroy(sectionId);
      });
    }
  };

  // Expose to global namespace
  window.CollectionIconsNew = CollectionIconsNewManager;

  /**
   * Auto-initialization
   */
  function autoInit() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        CollectionIconsNewManager.initAll();
      });
    } else {
      CollectionIconsNewManager.initAll();
    }
  }

  /**
   * Shopify Theme Editor Support
   */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target;
      if (section.classList.contains('collection-icons-new-section')) {
        const sectionId = section.dataset.sectionId;
        if (sectionId) {
          CollectionIconsNewManager.init(sectionId);
        }
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      const section = event.target;
      if (section.classList.contains('collection-icons-new-section')) {
        const sectionId = section.dataset.sectionId;
        if (sectionId) {
          CollectionIconsNewManager.destroy(sectionId);
        }
      }
    });

    document.addEventListener('shopify:section:select', function(event) {
      const section = event.target;
      if (section.classList.contains('collection-icons-new-section')) {
        const sectionId = section.dataset.sectionId;
        const instance = CollectionIconsNewManager.getInstance(sectionId);
        if (instance) {
          // Recalculate dimensions when section is selected
          setTimeout(() => {
            instance.calculateDimensions();
            instance.updateUI();
          }, 100);
        }
      }
    });
  } else {
    // Auto-initialize for regular page loads
    autoInit();
  }

  /**
   * Utility functions
   */
  window.CollectionIconsNew.utils = {
    /**
     * Debounce function
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Throttle function
     */
    throttle(func, limit) {
      let inThrottle;
      return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    /**
     * Check if element is in viewport
     */
    isInViewport(element) {
      const rect = element.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }
  };

})();

